import { getLanguageLabelByCode } from '../../constants/videoLanguages'

const isLikelyLanguageCode = value =>
  typeof value === 'string' && /^[a-z]{2,3}(-[a-z]{2})?$/i.test(value.trim())

const toTitleCase = value =>
  typeof value === 'string' && value.trim()
    ? value
        .trim()
        .split(/\s+/)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ')
    : ''

const isOpaqueVoiceIdentifier = value => {
  if (typeof value !== 'string') return false
  const normalized = value.trim()
  return (
    normalized.length >= 12 &&
    /^[a-z0-9]+$/i.test(normalized) &&
    /[a-z]/i.test(normalized) &&
    /\d/.test(normalized) &&
    !normalized.includes('_') &&
    !normalized.includes('-')
  )
}

const getNormalizedVoiceName = (voice, id, fallbackType, index) => {
  if (fallbackType === 'default') {
    return `Default Voice ${index + 1}`
  }

  const explicitName =
    voice.name || voice.voice_name || voice.display_name || voice.label

  if (explicitName) {
    return explicitName
  }

  if (typeof id === 'string' && !isOpaqueVoiceIdentifier(id)) {
    return id
  }

  return `${
    fallbackType === 'uploaded' ? 'Custom Voice' : 'System Voice'
  } ${index + 1}`
}

export const normalizeVoiceLanguages = languages => {
  if (!languages) return []

  const normalizedLanguages = Array.isArray(languages) ? languages : [languages]

  return normalizedLanguages
    .map(language => {
      if (!language) return null

      if (typeof language === 'string') {
        const normalized = language.trim()
        if (!normalized) return null
        return isLikelyLanguageCode(normalized)
          ? getLanguageLabelByCode(normalized.toLowerCase())
          : toTitleCase(normalized)
      }

      const code =
        language.code ||
        language.language_code ||
        language.lang ||
        language.value
      const name =
        language.name ||
        language.language ||
        language.label ||
        language.display_name

      if (name) return toTitleCase(name)
      if (code) return getLanguageLabelByCode(String(code).toLowerCase())
      return null
    })
    .filter(Boolean)
    .filter((language, index, allLanguages) => {
      return allLanguages.indexOf(language) === index
    })
}

export const normalizeVoiceEntry = (
  voice,
  fallbackType = 'default',
  index = 0
) => {
  if (!voice) return null

  if (typeof voice === 'string') {
    return {
      id: voice,
      name: fallbackType === 'default' ? `Default Voice ${index + 1}` : voice,
      type: fallbackType,
      description: '',
      languages: []
    }
  }

  const id =
    voice.id ||
    voice.voice_id ||
    voice.voiceId ||
    voice.name ||
    voice.voice_name

  if (!id) return null

  return {
    ...voice,
    id,
    name: getNormalizedVoiceName(voice, id, fallbackType, index),
    type: voice.type || fallbackType,
    description:
      voice.description || voice.voice_description || voice.prompt || '',
    languages: normalizeVoiceLanguages(
      voice.languages || voice.language || voice.verified_languages
    )
  }
}

export const normalizeVoiceList = (voices, fallbackType = 'default') =>
  (Array.isArray(voices) ? voices : [])
    .map((voice, index) => normalizeVoiceEntry(voice, fallbackType, index))
    .filter(Boolean)

export const mergeVoicesById = (...voiceCollections) => {
  const merged = new Map()

  voiceCollections.flat().forEach(voice => {
    if (!voice?.id) return
    const previous = merged.get(voice.id) || {}
    merged.set(voice.id, {
      ...previous,
      ...voice,
      languages: normalizeVoiceLanguages([
        ...(previous.languages || []),
        ...(voice.languages || [])
      ])
    })
  })

  return Array.from(merged.values())
}
